import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "~/lib/utils";

const VARIANTS = {
  newPrimary: "bg-sbPurple text-white hover:bg-sbPurple/80 hover:sbPurple/10",
  destructive: "border border-red-700 bg-transparent text-red-700 hover:bg-red-100",
  outline:
    "border border-border bg-transparent text-sbBlack bg-white hover:bg-muted hover:text-sbBlack",

  ghost: "hover:bg-muted hover:text-primary",
  secondary:
    "bg-white text-sbPurple hover:bg-sbPurple/10 border border-sbPurple hover:text-sbPurple",
} as const;

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: VARIANTS,
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "newPrimary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  variant: keyof typeof VARIANTS;
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          // if the button is an anchor tag, we want to keep the text color
          { "hover:text-primary-foreground": variant === "newPrimary" && asChild },
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
